import go from 'gojs';

// Define types for options and callbacks
export interface NodeTemplateOptions {
    readonly: boolean;
    direction: 'left' | 'right';
}

export interface CapsNodeTemplateCallbacks {
    onDeleteCapClick: (capId: string) => void;
    onCapClick?: (capId: string) => void;
}

export interface PinsNodeTemplateCallbacks {
    onDeletePinClick: (pinId: string) => void;
    onPinClick?: (pinId: string) => void;
}

export function createCapsNodeTemplate(options: NodeTemplateOptions, callbacks: CapsNodeTemplateCallbacks): go.Node {
    const $ = go.GraphObject.make;
    const isLeft = options.direction === 'left';

    return $(
        go.Node,
        go.Panel.Horizontal,
        {
            selectable: true,
            movable: false,
        },
        $(
            go.Panel,
            go.Panel.Auto,
            {
                margin: 1,
            },
            $(go.Shape, 'Rectangle', {
                fill: '#f5f5f5',
                stroke: '#cccccc',
                strokeWidth: 2,
                parameter1: 15,
                width: 160,
                // height: 30,
            }),
            $(
                go.Panel,
                go.Panel.Horizontal,
                {
                    margin: 1,
                    width: NaN,
                },
                // Left port for right cap only
                ...(!isLeft
                    ? [
                          $(
                              go.Panel,
                              go.Panel.Vertical,
                              {
                                  margin: 0,
                                  width: NaN,
                              },
                              $(
                                  go.Panel,
                                  go.Panel.Spot,
                                  {},
                                  $(go.Shape, 'Rectangle', {
                                      desiredSize: new go.Size(24, 24),
                                      stroke: '#1976d2',
                                      strokeWidth: 1,
                                      fill: 'transparent',
                                      geometryString: 'M0,0 L0,24 L24,24 L24,0 Z',
                                  }),
                                  $(go.Picture, {
                                      source: '/icons/link-variant.svg',
                                      width: 20,
                                      height: 20,
                                      cursor: 'pointer',
                                      margin: new go.Margin(0, 0, 0, 0),
                                      imageAlignment: go.Spot.Center,
                                      imageStretch: go.GraphObject.Uniform,
                                      fromLinkable: false,
                                      toLinkable: true,
                                      fromLinkableDuplicates: false,
                                      toLinkableDuplicates: true,
                                      portId: 'right-cap-left-side',
                                      scale: 1,
                                      click: (e: any, obj: any) => {
                                          const node = obj.part;
                                          const capId = node?.data?.key;
                                          if (capId) {
                                              callbacks.onDeleteCapClick(capId);
                                          }
                                      },
                                  })
                              )
                          ),
                      ]
                    : []),
                // Center: text and delete icon
                $(
                    go.Panel,
                    go.Panel.Vertical,
                    {
                        margin: 0,
                        width: 130,
                    },
                    $(
                        go.TextBlock,
                        {
                            margin: 0,
                            cursor: 'pointer',
                            click: (e: any, obj: any) => {
                                const node = obj.part;
                                const capId = node?.data?.key;
                                if (capId && callbacks.onCapClick) {
                                    callbacks.onCapClick(capId);
                                }
                            },
                        },
                        new go.Binding('text', 'text')
                    )
                    // $(go.Picture, {
                    //     source: '/icons/delete-empty-red.svg',
                    //     width: 20,
                    //     height: 20,
                    //     cursor: 'pointer',
                    //     margin: new go.Margin(0, 5, 0, 5),
                    //     imageAlignment: go.Spot.Center,
                    //     imageStretch: go.GraphObject.Uniform,
                    //     scale: 1,
                    //     click: (e: any, obj: any) => {
                    //         const node = obj.part;
                    //         const capId = node?.data?.key;
                    //         if (capId) {
                    //             callbacks.onDeleteCapClick(capId);
                    //         }
                    //     },
                    // })
                ),
                // Right port for left cap only
                ...(isLeft
                    ? [
                          $(
                              go.Panel,
                              go.Panel.Vertical,
                              {
                                  margin: 0,
                                  width: NaN,
                              },
                              $(
                                  go.Panel,
                                  go.Panel.Spot,
                                  {},
                                  $(go.Shape, 'Rectangle', {
                                      desiredSize: new go.Size(24, 24),
                                      stroke: '#1976d2',
                                      strokeWidth: 1,
                                      fill: 'transparent',
                                      geometryString: 'M0,0 L0,24 L24,24 L24,0 Z',
                                  }),
                                  $(go.Picture, {
                                      source: '/icons/link-variant.svg',
                                      width: 20,
                                      height: 20,
                                      cursor: 'pointer',
                                      margin: new go.Margin(0, 0, 0, 0),
                                      imageAlignment: go.Spot.Center,
                                      imageStretch: go.GraphObject.Uniform,
                                      fromLinkable: false,
                                      toLinkable: true,
                                      toLinkableDuplicates: true,
                                      fromLinkableDuplicates: false,
                                      toSpot: go.Spot.Right,
                                      portId: 'left-cap-right-side',
                                      scale: 1,
                                  })
                              )
                          ),
                      ]
                    : [])
            )
        )
    );
}

export function createConnectorPinsNodeTemplate(
    options: {direction: 'left' | 'right'; readonly: boolean},
    callbacks: {onDeleteConnectorPinClick: (pinId: string) => void}
): go.Node {
    const $ = go.GraphObject.make;
    const isLeft = options.direction === 'left';

    return $(
        go.Node,
        go.Panel.Horizontal,
        {
            selectable: true,
            movable: true,
        },
        $(
            go.Panel,
            go.Panel.Auto,
            {
                margin: 1,
            },
            $(go.Shape, 'Rectangle', {
                fill: '#f5f5f5',
                stroke: '#cccccc',
                strokeWidth: 2,
                parameter1: 15,
                width: 230,
            }),
            $(
                go.Panel,
                go.Panel.Horizontal,
                {
                    margin: 1,
                    width: NaN,
                },
                // Left port (only for left, or right, as per direction)
                $(
                    go.Panel,
                    go.Panel.Vertical,
                    {
                        margin: 0,
                        width: NaN,
                    },
                    ...(isLeft
                        ? [
                              $(
                                  go.Panel,
                                  go.Panel.Spot,
                                  {},
                                  $(go.Shape, 'Rectangle', {
                                      desiredSize: new go.Size(24, 24),
                                      stroke: '#1976d2',
                                      strokeWidth: 1,
                                      fill: 'transparent',
                                      geometryString: 'M0,0 L0,24 L24,24 L24,0 Z',
                                  }),
                                  $(go.Picture, {
                                      source: '/icons/link-variant.svg',
                                      width: 20,
                                      height: 20,
                                      cursor: 'pointer',
                                      margin: new go.Margin(0, 0, 0, 0),
                                      imageAlignment: go.Spot.Center,
                                      imageStretch: go.GraphObject.Uniform,
                                      fromLinkable: true,
                                      toLinkable: true,
                                      fromSpot: go.Spot.LeftSide,
                                      toSpot: go.Spot.LeftSide,
                                      portId: 'left-connector-pin-left-side',
                                  })
                              ),
                          ]
                        : [
                              $(
                                  go.Panel,
                                  go.Panel.Spot,
                                  {},
                                  $(go.Shape, 'Rectangle', {
                                      desiredSize: new go.Size(24, 24),
                                      stroke: '#1976d2',
                                      strokeWidth: 1,
                                      fill: 'transparent',
                                      geometryString: 'M0,0 L0,24 L24,24 L24,0 Z',
                                  }),
                                  $(go.Picture, {
                                      source: '/icons/link-variant.svg',
                                      width: 20,
                                      height: 20,
                                      cursor: 'pointer',
                                      margin: new go.Margin(0, 0, 0, 0),
                                      imageAlignment: go.Spot.Center,
                                      imageStretch: go.GraphObject.Uniform,
                                      fromLinkable: true,
                                      toLinkable: true,
                                      toLinkableDuplicates: true,
                                      fromLinkableDuplicates: true,
                                      fromSpot: go.Spot.LeftSide,
                                      toSpot: go.Spot.LeftSide,
                                      portId: 'right-connector-pin-left-side',
                                  })
                              ),
                          ])
                ),
                // Center: text, radio, delete
                $(
                    go.Panel,
                    go.Panel.Vertical,
                    {
                        margin: 0,
                        width: 174,
                    },
                    $(
                        go.TextBlock,
                        {
                            margin: 1,
                        },
                        new go.Binding('text', 'text')
                    ),
                    $(
                        go.Panel,
                        go.Panel.Horizontal,
                        {
                            margin: 0,
                            width: NaN,
                        },
                        $(
                            go.Shape,
                            'Circle',
                            {
                                desiredSize: new go.Size(14, 14),
                                stroke: 'black',
                                fill: 'white',
                                strokeWidth: 2,
                                cursor: 'pointer',
                                margin: new go.Margin(0, 5, 0, 10),
                            },
                            new go.Binding('fill', 'isActive', function (s) {
                                return s ? 'black' : 'white';
                            })
                        ),
                        $(go.Picture, {
                            source: '/icons/delete-empty-red.svg',
                            width: 20,
                            height: 20,
                            cursor: 'pointer',
                            margin: new go.Margin(0, 0, 0, 10),
                            imageAlignment: go.Spot.Center,
                            imageStretch: go.GraphObject.Uniform,
                            scale: 1,
                            click: (e: any, obj: any) => {
                                const node = obj.part;
                                const cPinId = node?.data?.key;
                                if (cPinId) {
                                    callbacks.onDeleteConnectorPinClick(cPinId);
                                }
                            },
                        })
                    )
                ),
                // Right port (only for left, or right, as per direction)
                $(
                    go.Panel,
                    go.Panel.Vertical,
                    {
                        margin: 0,
                        width: NaN,
                    },
                    ...(isLeft
                        ? [
                              $(
                                  go.Panel,
                                  go.Panel.Spot,
                                  {},
                                  $(go.Shape, 'Rectangle', {
                                      desiredSize: new go.Size(24, 24),
                                      stroke: '#1976d2',
                                      strokeWidth: 1,
                                      fill: 'transparent',
                                      geometryString: 'M0,0 L0,24 L24,24 L24,0 Z',
                                  }),
                                  $(go.Picture, {
                                      source: '/icons/link-variant.svg',
                                      width: 20,
                                      height: 20,
                                      cursor: 'pointer',
                                      margin: new go.Margin(0, 0, 0, 0),
                                      imageAlignment: go.Spot.Center,
                                      imageStretch: go.GraphObject.Uniform,
                                      fromLinkable: false,
                                      toLinkable: true,
                                      toLinkableDuplicates: true,
                                      fromLinkableDuplicates: true,
                                      toMaxLinks: 1,
                                      fromSpot: go.Spot.Right,
                                      portId: 'left-connector-pin-right-side',
                                  })
                              ),
                          ]
                        : [
                              $(
                                  go.Panel,
                                  go.Panel.Spot,
                                  {},
                                  $(go.Shape, 'Rectangle', {
                                      desiredSize: new go.Size(24, 24),
                                      stroke: '#1976d2',
                                      strokeWidth: 1,
                                      fill: 'transparent',
                                      geometryString: 'M0,0 L0,24 L24,24 L24,0 Z',
                                  }),
                                  $(go.Picture, {
                                      source: '/icons/link-variant.svg',
                                      width: 20,
                                      height: 20,
                                      cursor: 'pointer',
                                      margin: new go.Margin(0, 0, 0, 0),
                                      imageAlignment: go.Spot.Center,
                                      imageStretch: go.GraphObject.Uniform,
                                      fromLinkable: false,
                                      toLinkable: true,
                                      toLinkableDuplicates: false,
                                      toMaxLinks: 1,
                                      fromSpot: go.Spot.Right,
                                      portId: 'right-connector-pin-right-side',
                                  })
                              ),
                          ])
                )
            )
        )
    );
}

export function createPinsNodeTemplate(options: NodeTemplateOptions, callbacks: PinsNodeTemplateCallbacks): go.Node {
    const $ = go.GraphObject.make;

    const isLeft = options.direction === 'left';
    const portIdLeft = isLeft ? 'left-pin-left-side' : 'right-pin-left-side';
    const portIdRight = isLeft ? 'left-pin-right-side' : 'right-pin-right-side';

    return $(
        go.Node,
        go.Panel.Horizontal,
        {
            selectable: true,
            movable: true,
            // linkValidation: (fromNode: go.Node, fromPort: go.GraphObject, toNode: go.Node, toPort: go.GraphObject) => {
            //     fromNode.findLinksConnected().each(l => {
            //         console.log(l.toPortId, l.fromPortId, l.portId);
            //     });
            //     return fromNode.findLinksConnected(fromPort.portId).count + toNode.findLinksConnected(toPort.portId).count < 1;
            // },
        },
        $(
            go.Panel,
            go.Panel.Auto,
            {
                margin: 1,
            },
            $(go.Shape, 'Rectangle', {
                fill: '#f5f5f5',
                stroke: '#cccccc',
                strokeWidth: 1,
                parameter1: 15,
                width: 160,
                // height: 130,
            }),
            $(
                go.Panel,
                go.Panel.Horizontal,
                {
                    margin: 1,
                    width: NaN,
                },
                $(
                    go.Panel,
                    go.Panel.Vertical,
                    {
                        margin: 0,
                        width: NaN,
                    },
                    // Add border to each Picture by wrapping in a Panel with a Shape as border
                    ...Array.from({length: 1}).map((_, idx) =>
                        $(
                            go.Panel,
                            go.Panel.Spot,
                            {},
                            $(go.Shape, 'Rectangle', {
                                desiredSize: new go.Size(24, 24),
                                stroke: '#1976d2',
                                strokeWidth: 1,
                                fill: 'transparent',
                                geometryString:
                                    idx === 0
                                        ? // All sides for first index
                                          'M0,0 L0,24 L24,24 L24,0 Z'
                                        : // Only left, right, bottom for others
                                          'M0,0 L0,110 L200,110 L200,0',
                            }),
                            $(go.Picture, {
                                source: '/icons/link-variant.svg',
                                width: 20,
                                height: 20,
                                cursor: 'pointer',
                                margin: new go.Margin(0, 0, 0, 0),
                                imageAlignment: go.Spot.Center,
                                imageStretch: go.GraphObject.Uniform,
                                scale: 1,
                                fromLinkable: true,
                                toLinkable: true,
                                toLinkableDuplicates: false,
                                toMaxLinks: 1,
                                fromSpot: go.Spot.LeftSide,
                                toSpot: go.Spot.LeftSide,
                                portId: isLeft ? `left-pin-left-side` : `right-pin-left-side`,
                            })
                        )
                    )
                ),
                $(
                    go.Panel,
                    go.Panel.Vertical,
                    {
                        margin: 0,
                        width: 100,
                    },
                    $(
                        go.TextBlock,
                        {
                            margin: 0,
                            cursor: 'pointer',
                            click: (e: any, obj: any) => {
                                const node = obj.part;
                                const pinId = node?.data?.key;
                                if (pinId && callbacks.onPinClick) {
                                    callbacks.onPinClick(pinId);
                                }
                            },
                        },
                        new go.Binding('text', 'text')
                    ),
                    $(
                        go.Panel,
                        go.Panel.Horizontal,
                        {
                            margin: 0,
                            width: NaN,
                        },
                        $(
                            go.Shape,
                            'Circle',
                            {
                                desiredSize: new go.Size(14, 14),
                                stroke: 'black',
                                fill: 'white',
                                strokeWidth: 2,
                                cursor: 'pointer',
                                margin: new go.Margin(0, 0, 0, 0),
                            },
                            new go.Binding('fill', 'isActive', function (s) {
                                return s ? 'black' : 'white';
                            })
                        ),
                        $(go.Picture, {
                            source: '/icons/delete-empty-red.svg',
                            width: 20,
                            height: 20,
                            cursor: 'pointer',
                            margin: new go.Margin(0, 5, 0, 5),
                            imageAlignment: go.Spot.Center,
                            imageStretch: go.GraphObject.Uniform,
                            scale: 1,
                            click: (e: any, obj: any) => {
                                const node = obj.part;
                                const pinId = node?.data?.key;
                                if (pinId) {
                                    callbacks.onDeletePinClick(pinId);
                                }
                            },
                        })
                    )
                ),
                $(
                    go.Panel,
                    go.Panel.Vertical,
                    {
                        margin: 0,
                        width: NaN,
                    },
                    // Add border to each Picture by wrapping in a Panel with a Shape as border
                    ...Array.from({length: 1}).map((_, idx) =>
                        $(
                            go.Panel,
                            go.Panel.Spot,
                            {},
                            $(go.Shape, 'Rectangle', {
                                desiredSize: new go.Size(24, 24),
                                stroke: '#1976d2',
                                strokeWidth: 1,
                                fill: 'transparent',
                                geometryString:
                                    idx === 0
                                        ? // All sides for first index
                                          'M0,0 L0,24 L24,24 L24,0 Z'
                                        : // Only left, right, bottom for others
                                          'M0,0 L0,110 L200,110 L200,0',
                            }),
                            $(go.Picture, {
                                source: '/icons/link-variant.svg',
                                width: 20,
                                height: 20,
                                cursor: 'pointer',
                                margin: new go.Margin(0, 0, 0, 0),
                                imageAlignment: go.Spot.Center,
                                imageStretch: go.GraphObject.Uniform,
                                scale: 1,
                                fromLinkable: true,
                                toLinkable: true,
                                toLinkableDuplicates: false,
                                toMaxLinks: 1,
                                fromSpot: go.Spot.RightSide,
                                toSpot: go.Spot.RightSide,
                                portId: isLeft ? `left-pin-right-side` : `right-pin-right-side`,
                            })
                        )
                    )
                )
            ),
            $(go.Panel, go.Panel.Horizontal, {
                margin: 1,
                width: NaN,
            })
        )
    );
}

export function setupLinkTemplate() {
    const $ = go.GraphObject.make;
    return $(go.Link, {curve: go.Link.Bezier, layerName: 'Foreground'}, $(go.Shape, {strokeWidth: 3}));
}

export function createGroupTemplate(groupName: string) {
    const $ = go.GraphObject.make;
    return $(
        go.Group,
        go.Panel.Vertical,
        {
            layout: $(go.GridLayout, {
                wrappingColumn: 1, // Ensure nodes are aligned into a single column
                alignment: go.GridLayout.Position,
                cellSize: new go.Size(1, 1),
                spacing: new go.Size(0, 1), // Adjust vertical spacing between nodes
                comparer: (a: go.Part, b: go.Part) => {
                    const aOrder = a.data.sortOrder ?? 0;
                    const bOrder = b.data.sortOrder ?? 0;
                    return aOrder - bOrder;
                },
            }),
            isSubGraphExpanded: true,
            computesBoundsAfterDrag: true,
            handlesDragDropForMembers: true,
            mouseDragEnter: (e, group: any) => {
                group.isHighlighted = true;
            },
            mouseDragLeave: (e, group: any) => {
                group.isHighlighted = false;
            },
            mouseDrop: (e, group: any) => {
                const tool = e.diagram.toolManager.draggingTool;
                const draggedParts = tool.draggedParts;
                if (draggedParts) {
                    draggedParts.each(part => {
                        if (part instanceof go.Node) {
                            part.containingGroup = group as any;
                        }
                    });
                }
                group.isHighlighted = false;
                group.layout.invalidateLayout(); // Trigger layout rearrangement

                // Sort nodeDataArray data of the group based on sortOrder
                const groupKey = group.data.key;
                // const groupNodes = me.myDiagram!.model.nodeDataArray.filter((node: any) => node.group === groupKey);

                // groupNodes.sort((a: any, b: any) => {
                //     const nodeA = me.myDiagram!.findNodeForData(a);
                //     const nodeB = me.myDiagram!.findNodeForData(b);
                //     return (nodeA?.location.y ?? 0) - (nodeB?.location.y ?? 0);
                // });

                // groupNodes.forEach((node: any, index: number) => {
                //     // node.sortOrder = index; // Update sortOrder locally
                //     e.diagram.model.setDataProperty(node, 'sortOrder', index);
                // });
            },
        },
        $(go.TextBlock, {font: 'bold 12pt sans-serif'}, new go.Binding('text', 'text')),
        $(
            go.Panel,
            go.Panel.Auto,
            $(
                go.Panel,
                go.Panel.Auto,
                $(go.Shape, 'Rectangle', {
                    fill: 'rgba(200, 200, 200, 0.33)',
                    margin: 0,
                    stroke: '#cccccc',
                    strokeWidth: 2,
                }),
                new go.Placeholder({padding: 5}) // Add padding inside the rectangle
            )
        )
    );
}
